/**
 * The stripper for a fabricated action-outcome line, for every client.
 *
 * The outcome format belongs to the APP: `describeActionOutcome` writes it when
 * a learner accepts or declines a proposal, and it is fed back to the model as
 * history so the tutor knows what already happened. The model then sometimes
 * IMITATES it instead of emitting a real `[ACTION{...}]` token, writing its own
 * "the learner confirmed it and it completed successfully" line about something
 * that never happened.
 *
 * Two things go wrong if that line is left in place, and the second is the worse
 * one:
 *
 *   1. The learner reads raw protocol text in a chat bubble.
 *   2. It survives into the replayed history, where the model reads its own
 *      invention back as though the app had written it - so the hallucination is
 *      reinforced, and a REAL proposal for the same action can look already-done
 *      and be skipped on the next turn.
 *
 * This lived only in the web parser. Native had no equivalent, so both failure
 * modes were live on the phone against the same prompt and the same route. It is
 * split out here, as its own zero-import leaf, because the web module around it
 * value-imports zod, the route table, the activities registry and the
 * preferences schema, none of which can (or should) cross into a mobile bundle.
 */

/**
 * Matches an app-format outcome line anywhere in the reply, with the whitespace
 * on either side of it, so removing one from mid-sentence does not leave a gap.
 *
 * Deliberately NOT anchored: the model puts these before the prose, after it and
 * in the middle of it. `[^\]]*` stops at the first closing bracket, so ordinary
 * bracketed prose elsewhere in the reply is untouched.
 */
export const FABRICATED_OUTCOME_RE = /\s*\[proposed action:[^\]]*\]\s*/g;

/**
 * Remove every fabricated outcome line from text the learner will read.
 *
 * The second replace collapses the run of spaces a removal leaves between two
 * words; the trim handles a line that was the whole reply.
 */
export function stripFabricatedOutcomeLines(text: string): string {
  return text
    .replace(FABRICATED_OUTCOME_RE, " ")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}
